<template>
  <div v-if="mode === Mode.View" class="d-flex flex-column ga-2">
    <div class="d-flex flex-column">
      <v-label>{{ t("form.address.line1") }}</v-label>
      <p class="pb-2 pt-2">
        {{ model.line1 }}
      </p>
    </div>

    <div class="d-flex flex-column">
      <v-label>{{ t("form.address.line2") }}</v-label>
      <p class="pb-2 pt-2">
        {{ model.line2 }}
      </p>
    </div>

    <div :class="`d-flex ${mdAndUp ? 'flex-row' : 'flex-column'} ga-2`">
      <div class="d-flex flex-column" style="width: 50%">
        <v-label>{{ t("form.address.city") }}</v-label>
        <p class="pb-2 pt-2">
          {{ model.city }}
        </p>
      </div>
      <div class="d-flex flex-column" style="width: 50%">
        <v-label>{{ t("form.address.stateOrProvince") }}</v-label>
        <p class="pb-2 pt-2">
          {{ model.stateOrProvince }}
        </p>
      </div>
    </div>

    <div :class="`d-flex ${mdAndUp ? 'flex-row' : 'flex-column'} ga-2`">
      <div class="d-flex flex-column" style="width: 50%">
        <v-label>{{ t("form.address.postalCode") }}</v-label>
        <p class="pb-2 pt-2">
          {{ model.postalCode }}
        </p>
      </div>
      <div class="d-flex flex-column" style="width: 50%">
        <v-label>{{ t("form.address.country") }}</v-label>
        <p class="pb-2 pt-2">
          {{ tradedCountries.find((c) => c.code === model.country)?.name ?? "United Kingdom" }}
        </p>
      </div>
    </div>

    <div>
      <y-btn class="mb-2" @click="mode = Mode.Edit">
        {{ t("form.edit") }}
      </y-btn>
    </div>
  </div>

  <div v-else class="d-flex flex-column ga-2">
    <y-text-field
      v-model="model.line1"
      autocapitalize
      :label="t('form.address.line1')"
      :error-messages="validationErrors(props.validator.line1.$errors)"
    />

    <y-text-field v-model="model.line2" autocapitalize :label="t('form.address.line2')" />

    <div :class="`d-flex ${mdAndUp ? 'flex-row' : 'flex-column'} ga-2`">
      <y-text-field
        v-model="model.city"
        autocapitalize
        :label="t('form.address.city')"
        style="width: 50%"
      />

      <y-text-field
        v-model="model.stateOrProvince"
        autocapitalize
        :label="t('form.address.stateOrProvince')"
        style="width: 50%"
      />
    </div>

    <div :class="`d-flex ${mdAndUp ? 'flex-row' : 'flex-column'} ga-2`">
      <y-text-field
        v-model="model.postalCode"
        autocapitalize
        :label="t('form.address.postalCode')"
        style="width: 50%"
        :error-messages="validationErrors(props.validator.postalCode.$errors)"
      />

      <y-autocomplete
        v-model="model.country"
        :items="tradedCountries"
        item-title="name"
        item-value="code"
        :label="t('form.address.country')"
        style="width: 50%"
        :error-messages="validationErrors(props.validator.country.$errors)"
        :disabled="true"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { tradedCountries } from "@/utils/config";
import { validationErrors } from "@/utils/format";

const { t } = useI18n();
const { mdAndUp } = useDisplay();

enum Mode {
  Edit = "edit",
  View = "view",
}

const model = defineModel<{
  line1: string;
  line2: string | undefined;
  city: string;
  stateOrProvince: string | undefined;
  postalCode: string;
  country: string;
}>({
  required: true,
});
const isAddress = computed(
  () =>
    [
      model.value?.line1,
      model.value?.line2,
      model.value?.city,
      model.value?.stateOrProvince,
      model.value?.postalCode,
    ]
      .filter((line) => !!line)
      .join("").length > 0,
);
const mode = ref<Mode>(isAddress.value ? Mode.View : Mode.Edit);
const props = defineProps<{
  validator: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
}>();
</script>
